/*
 * Shared option logic for the product drawer (sections/bundle-product-
 * drawer.liquid), used by BOTH pages that open it: the bundle builder
 * (assets/bundle-builder.js) and the product page's "Complete the look"
 * block (assets/complete-look.js).
 *
 * It lived inside bundle-builder.js until "Complete the look" started
 * reusing the same drawer body; a second copy there would have been a
 * second place to fix every availability bug, so it moved here instead.
 * The `change` listener is bound on `document` — one binding covers every
 * drawer on the page, whoever opened it.
 *
 * Exposes window.BundleDrawerOptions.recompute(productEl) for callers that
 * need to re-derive availability outside a `change` event (right after a
 * drawer body is swapped in, for instance).
 */
(function () {
  if (window.BundleDrawerOptions) return;

  /*
   * After ANY option radio changes inside the drawer, re-derives every
   * fieldset's availability — and each radio's matching variant — against
   * the product's full variant list (task-5 finding 2). A forward-only
   * cascade (the PDP's assets/global.js VariantSelects.updateVariantStatuses,
   * which only updates a LATER fieldset from an EARLIER one's checked
   * value) is not enough here: this store models colour as a separate
   * product, so a drawer's remaining options are ordinary Shopify options
   * (e.g. Size + length on soft-loose-pants-island-stripes) that constrain
   * each other in BOTH directions — changing length must re-enable Size
   * values whose only available combination is with the newly picked
   * length, not just leave Size showing whatever was true when the drawer
   * was first rendered.
   *
   * Reuses the PDP's underlying technique (a `<script type="application/
   * json">` of the variant list, read once and filtered client-side) via
   * the data-bundle-variants tag sections/bundle-product-drawer.liquid
   * embeds, rather than inventing a parallel data source.
   */
  function recomputeOptionAvailability(product) {
    var variantsEl = product.querySelector('[data-bundle-variants]');
    if (!variantsEl) return;

    var variants;
    try {
      variants = JSON.parse(variantsEl.textContent);
    } catch (e) {
      return;
    }

    var fieldsets = Array.from(product.querySelectorAll('[data-option-index]'));
    if (!fieldsets.length) return;

    /* What is currently checked in every fieldset, keyed by option index
       (0-based — matches the position Shopify's own `variant.options`
       array uses, same convention as the Liquid this mirrors). */
    var selected = {};
    fieldsets.forEach(function (fieldset) {
      var index = Number(fieldset.getAttribute('data-option-index'));
      var checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked) selected[index] = checked.value;
    });

    fieldsets.forEach(function (fieldset) {
      var index = Number(fieldset.getAttribute('data-option-index'));

      fieldset.querySelectorAll('input[type="radio"]').forEach(function (radio) {
        /* Same "matched vs. fallback" logic bundle-product-drawer.liquid
           runs at render time, run again here now that the selection has
           moved on: prefer the variant that also matches every OTHER
           fieldset's CURRENT selection; fall back to any variant with this
           value if none does, so a genuinely nonexistent combination still
           resolves to something rather than to nothing. */
        var matched = null;
        var fallback = null;

        variants.forEach(function (variant) {
          if (variant.options[index] !== radio.value) return;
          if (!fallback) fallback = variant;
          if (matched) return;

          var matchesOthers = Object.keys(selected).every(function (otherIndex) {
            return Number(otherIndex) === index || variant.options[otherIndex] === selected[otherIndex];
          });
          if (matchesOthers) matched = variant;
        });

        var resolved = matched || fallback;
        var available = Boolean(resolved && resolved.available);

        /*
         * Two mechanisms, deliberately kept in sync: the real `disabled`
         * attribute (keeps the input actually unclickable/unfocusable —
         * ruling 4's "gasim, ne pryachem" behaviour) and the theme's own
         * `variant-disabled` LABEL class, which is what actually paints the
         * dimmed look here (`.product__variant-picker .variant-disabled`,
         * assets/section-main-product-slider.css.liquid) — the product
         * page keys its unavailable styling off that class, not off
         * `:disabled`, so setting only the attribute would leave the label
         * looking selectable. Radio and label are SIBLINGS in this markup
         * (sections/bundle-product-drawer.liquid), matched by `id`/`for`,
         * not label-wraps-input, so `closest()` cannot find the label —
         * hence the explicit `for="…"` lookup.
         */
        radio.disabled = !available;
        if (resolved) {
          radio.setAttribute('data-variant-id', resolved.id);
          radio.setAttribute('data-price', resolved.price);
          radio.setAttribute('data-image', resolved.image);
        }

        var label = fieldset.querySelector('label[for="' + CSS.escape(radio.id) + '"]');
        if (label) label.classList.toggle('variant-disabled', !available);
      });
    });

    /* The confirm button reflects the EXACT combination currently checked
       across every fieldset — not merely whichever radio just fired
       `change` — so a two-option change (ruling 5 / finding 2) always
       lands the real matching variant (e.g. S + tall), never a stale
       one-option-old guess. */
    var exactVariant = variants.find(function (variant) {
      return Object.keys(selected).every(function (index) {
        return variant.options[index] === selected[index];
      });
    });

    var confirmButton = product.querySelector('[data-bundle-confirm]');
    if (!confirmButton || !exactVariant) return;
    confirmButton.setAttribute('data-variant-id', exactVariant.id);
    confirmButton.setAttribute('data-price', exactVariant.price);
    confirmButton.setAttribute('data-image', exactVariant.image);
    /*
     * Availability is recorded as an attribute as well as applied, because
     * `disabled` has a second owner: the confirm handler disables this button
     * while its cart request is in flight. Radios stay live during that
     * request, so a size change landing mid-flight must not re-enable it —
     * and when the request finishes, the handler needs somewhere to read
     * whether re-enabling is right at all, or whether the variant now on the
     * button is sold out.
     */
    confirmButton.toggleAttribute('data-bundle-unavailable', !exactVariant.available);
    confirmButton.disabled = !exactVariant.available || confirmButton.hasAttribute('data-bundle-loading');

    /*
     * The label follows availability the way the product page's own button
     * does (assets/global.js swaps `.atc-label` there). Both strings are
     * rendered onto the span by sections/bundle-product-drawer.liquid — the
     * shop's translations are not reachable from JS otherwise.
     */
    var label = confirmButton.querySelector('.atc-label');
    if (!label) return;

    var available = label.getAttribute('data-label-available');
    var soldOut = label.getAttribute('data-label-sold-out');
    if (!available || !soldOut) return;

    label.textContent = exactVariant.available ? available : soldOut;
  }

  /*
   * Keeps the confirm button and every fieldset's disabled/data-variant-id
   * state in sync with whichever size (or other non-colour option) radio
   * is currently checked. The "selected" pill look needs no class toggle
   * here — the drawer's fieldsets reuse the product page's own
   * `input[type='radio']:checked + label` rule (assets/section-main-
   * product-slider.css.liquid), which reflects the radio's native
   * `:checked` state directly; nothing to keep in sync by hand. Colour is
   * not a radio here: it is a full drawer swap (the click handler above),
   * not an in-place option change.
   */
  document.addEventListener('change', function (event) {
    var radio = event.target.closest('[data-bundle-product] input[type="radio"]');
    if (!radio) return;

    var product = radio.closest('[data-bundle-product]');
    if (!product) return;

    recomputeOptionAvailability(product);
  });

  window.BundleDrawerOptions = { recompute: recomputeOptionAvailability };
})();
